import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Globalize } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellType, GridPanel } from '@grapecity/wijmo.grid'

import { BaseComponent, Grid, HrGoal } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'

@Component({
    selector: 'app-user-metrics',
    templateUrl: './user-metrics.component.html',
    standalone: true,
    imports: [FormsModule, ActionToolbarCount, ActionToolbarSearchComponent, WjGridModule, WjGridFilterModule, GridSelectionCalculationComponent],
})
export class UserMetricsComponent extends BaseComponent {
    faSearch = faSearch

    grid: Grid<Partial<HrGoal>>

    weekCols = [] as string[]

    protected goalDirectionMap = new Map<number, string>([
        [0, '>='],
        [1, '<='],
        [2, '>= <='],
    ])

    constructor() {
        super()
        this.grid = new Grid<Partial<HrGoal>>(this.services)
            .filterBy(item => [item.name])
            .doInitialSelection(false)
            .isReadOnly(true)
            .wrapColumnHeaders()
        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            'MetricsRefresh',
            true,
            this.services.data.load(true, true, this.services.data.hrGoals.queryAllByAction('GetByUserId')).subscribe({
                next: result => {
                    const metrics = result[0] as HrGoal[]
                    this.grid.items = metrics
                    this.generateColumns()
                },
            }),
        )
    }

    generateColumns() {
        this.weekCols = []
        setTimeout(() => {
            const cols = [] as string[]

            let indexOfMostWeekValues = 0
            let weekValuesLength = 0
            for (let i = 0; i < this.grid.items.length; i++) {
                const currentItemLength = Object.keys(this.grid.items[i].weekValues || {}).length
                if (currentItemLength > weekValuesLength) {
                    indexOfMostWeekValues = i
                    weekValuesLength = currentItemLength
                }
            }

            if (this.grid.items.length > 0 && this.grid.items[indexOfMostWeekValues].weekValues) {
                const weeks = this.grid.items[indexOfMostWeekValues].weekValues
                for (const prop in weeks) {
                    cols.push(prop)
                }
            }
            this.weekCols = cols
        }, 1)
    }

    goalsGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c] as any
            const row = panel.rows[r]
            const item = row.dataItem as HrGoal
            cell.classList.remove('red-cell')
            cell.classList.remove('green-cell')

            if (col.binding === 'goal1') {
                if (item.goalDirection < 2) {
                    cell.innerHTML = Globalize.format(item.goal1, item.format)
                } else {
                    cell.innerHTML = Globalize.format(item.goal1, item.format) + ' - ' + Globalize.format(item.goal2, item.format)
                }
            }

            if (col.binding === 'average') {
                cell.innerHTML = Globalize.format(item.average, item.format)
                if (!item.showAverage) cell.innerHTML = ''
            }

            if (col.binding === 'sum') {
                cell.innerHTML = Globalize.format(item.sum, item.format)
                // Don't show the sum for percents, since it doesn't really make sense.
                if (item.format[0].toUpperCase() === 'P' || !item.showSum) cell.innerHTML = ''
            }

            // Handle weekly columns (binding starts with 'weekValues.')
            if (col.binding && col.binding.startsWith('weekValues.')) {
                const weekKey = col.binding.split('.')[1]
                const originalVal = item.weekValues?.[weekKey]
                if (originalVal != null) {
                    cell.innerHTML = Globalize.format(originalVal, item.format)
                    // Apply goal meeting color
                    this.applyGoalColor(cell, originalVal, item)
                }
            }
        }
    }

    getCurrentValue(item: HrGoal): number | null {
        if (!item.weekValues) return null
        // Get the most recent non-null value
        const values = Object.values(item.weekValues).filter(v => v != null) as number[]
        return values.length > 0 ? values[0] : null
    }

    applyGoalColor(cell: HTMLDivElement, value: number, item: HrGoal) {
        switch (item.goalDirection) {
            case 0:
                if (value >= item.goal1) cell.classList.add('green-cell')
                else cell.classList.add('red-cell')
                break
            case 1:
                if (value <= item.goal1) cell.classList.add('green-cell')
                else cell.classList.add('red-cell')
                break
            case 2:
                if (value >= item.goal1 && value <= item.goal2) cell.classList.add('green-cell')
                else cell.classList.add('red-cell')
                break
        }
    }

    getGoalDirectionDisplay(item: HrGoal): string {
        return this.goalDirectionMap.get(item.goalDirection) || ''
    }
}
